import React, { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import { Calendar, Views, dayjsLocalizer } from 'react-big-calendar';
import { Box, Typography, Paper, IconButton, CircularProgress } from '@mui/material';
import events from './events';
import Layout from 'Component/Layouts/layout';
import useCredentials from 'hooks/useCredentials';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import FormModal from 'Component/FormModal';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import timezone from 'dayjs/plugin/timezone';
import UploadTrainingForm from 'Component/MultiStepTrainingForms/TrainingForm/UploadTrainingForm';
import ViewTrainingEvents from 'Component/MultiStepTrainingForms/TrainingForm/ViewTrainingEvents';
import { useDispatch, useSelector } from 'react-redux';
import { getCalendarData } from 'reducer/trainingCalendarSlice';
import { Resizable } from 're-resizable';
dayjs.extend(timezone);

const djLocalizer = dayjsLocalizer(dayjs);

const ColoredDateCellWrapper = ({ children }) =>
  React.cloneElement(React.Children.only(children), {
    style: {
      backgroundColor: 'lightblue',
    },
  });

const CustomToolbar = (toolbar) => {
  const goToBack = () => {
    toolbar.onNavigate('PREV');
  };

  const goToNext = () => {
    toolbar.onNavigate('NEXT');
  };

  const goToToday = () => {
    toolbar.onNavigate('TODAY');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}
    >
      <Typography variant="h6" component="div">
        {dayjs(toolbar.date).format('MMMM YYYY')}
      </Typography>
      <Box>
        <IconButton onClick={goToBack}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton onClick={goToToday}>Today</IconButton>
        <IconButton onClick={goToNext}>
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

const returnTitles = (isLearningSuperUser) => ({
  title: 'Create Training Batch',
  components: isLearningSuperUser
    ? [
        <FormModal
          buttonName={'Upload/Modify Training'}
          startIcon={<EditIcon />}
          width={470}
          form={
            <UploadTrainingForm
              employee={[]}
              disabled={['employee']}
              trigger={() => {
                // Trigger actions if needed
              }}
            />
          }
        />,
      ]
    : [],
});

// Add custom styles for calendar
const calendarStyles = {
  '.rbc-calendar': {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  '.rbc-month-view': {
    height: '100%',
  },
  '.rbc-month-row': {
    minHeight: '80px',
  },
  '.rbc-header': {
    padding: '5px 3px',
    fontSize: '12px',
  },
  '.rbc-date-cell': {
    padding: '2px 3px',
    fontSize: '12px',
    width: '30px',
    textAlign: 'center',
  },
  '.rbc-row-content': {
    minHeight: '60px',
  },
  '.rbc-event': {
    padding: '1px 3px',
    fontSize: '11px',
    borderRadius: '2px',
  },
  '.rbc-today': {
    backgroundColor: '#e6f7ff',
  },
  '.rbc-month-view table': {
    tableLayout: 'fixed',
  },
  '.rbc-month-view table td': {
    width: '14.28%',
  },
  '.rbc-month-view table th': {
    width: '14.28%',
  },
};

export default function ScheduleTraining({ ...props }) {
  const dispatch = useDispatch();
  const { userData: user } = useCredentials();
  const isLearningSuperUser = user?.learningRoles?.includes('admin');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const events = useSelector((state) => state.calendarSlice.data.data);

  const formattedEvents = events?.map((event) => ({
    ...event,
    start: new Date(event.start),
    end: new Date(event.end),
  }));

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await dispatch(getCalendarData());
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  const { components, defaultDate, max } = useMemo(
    () => ({
      components: {
        timeSlotWrapper: ColoredDateCellWrapper,
        toolbar: CustomToolbar, // Use the custom toolbar here
      },
      defaultDate: new Date(),
      max: dayjs().endOf('day').subtract(1, 'hours').toDate(),
    }),
    []
  );

  const views = ['month', 'agenda']; // Restrict views to "month" and "agenda"

  // Needed the functionality to show all the events of the day even if only one is clicked. hence meade a common function.
  const handleSelect = (selectedStart) => {
    const selectedDateEvents = events.filter(
      (event) =>
        dayjs(event.start).isSame(selectedStart, 'day') ||
        dayjs(event.end).isSame(selectedStart, 'day')
    );
    setSelectedEvent(selectedDateEvents);
  };

  return (
    <Layout>
      <Box
        sx={{
          fontSize: '24px',
          fontWeight: 500,
          background: 'linear-gradient(to bottom, #E9FBE0 0%, #E6F0E8 100%)',
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          padding: '16px',
          borderBottom: '1px solid #ccc',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Typography variant="h5" component="div">
          Training Calendar
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {returnTitles(isLearningSuperUser).components.map((elem, index) => (
            <Box key={index}>{elem}</Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          padding: '16px',
          gap: '16px',
          height: { xs: 'auto', md: 'calc(100vh - 200px)' },
          position: 'relative',
          overflow: { xs: 'visible', md: 'hidden' },
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: '1400px',
          margin: '0 auto',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            flex: { xs: 'none', md: '1 1 auto' },
            padding: '12px',
            overflow: 'hidden',
            borderRadius: '8px',
            minWidth: { xs: '0', md: '300px' },
            maxWidth: { xs: '100%', md: '800px' },
            width: { xs: '100%', md: '60%' },
            height: { xs: '450px', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            boxSizing: 'border-box',
            ...calendarStyles,
          }}
        >
          <Typography variant="h6" component="div" gutterBottom>
            Calendar View
          </Typography>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: '100%', overflow: 'auto' }}>
                <Calendar
                  components={components}
                  defaultDate={defaultDate}
                  events={formattedEvents}
                  localizer={djLocalizer}
                  max={max}
                  step={60}
                  views={views}
                  showMultiDayTimes
                  selectable
                  showAllEvents={true}
                  onSelectEvent={(selectedEvent) => handleSelect(selectedEvent.start)}
                  onSelectSlot={(slotInfo) => handleSelect(slotInfo.start)}
                />
              </Box>
            )}
          </Box>
        </Paper>
        <Resizable
          defaultSize={{
            width: '35%',
            height: '100%',
          }}
          minWidth="280px"
          maxWidth="500px"
          enable={{
            top: false,
            right: true,
            bottom: false,
            left: true,
            topRight: false,
            bottomRight: false,
            bottomLeft: false,
            topLeft: false,
          }}
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            flex: '0 0 auto',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              height: '100%',
              padding: '12px',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#f5f5f5',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            <Typography variant="h6" component="div" gutterBottom>
              Event Details
            </Typography>
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              <ViewTrainingEvents event={selectedEvent} />
            </Box>
          </Paper>
        </Resizable>
      </Box>
    </Layout>
  );
}
